import React, { useCallback, useEffect, useRef } from "react";
import { H3, IconButton, IconToggle, IOption, ITheme, Menu, Popover } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { css, useTheme } from "styled-components";

import { MapControlContainer } from "../../styled";
import { BasemapListBlock, BasemapListContainer, BasemapListTitle } from "./styled";
import { BasemapBlock, BaseMapItem } from "./BasemapBlock";
import { Legend } from "./Legend";
import { getShowMapLegend } from "../../../../ducks/layout/selectors";
import { toggleMapLegend } from "../../../../ducks/layout/layout";
import { useToggle } from "../../../../hooks/useToggle";
import { useAddBasemapHandlers } from "./hooks/useAddBasemapHandlers";
import { useNirvanaMode } from "../../../../hooks/layout/useNirvanaMode";
import { useIsDashboard } from "../../../../hooks/location/useIsDashboard";
import { useIsShared } from "../../../../hooks/api/useIsShared";

interface IBaseMapSelectProps {
  items: BaseMapItem[];
  baseMapName: string;
  updateMap: (name: string) => void;
}

const getPopoverMixin = (borderRadius: string) => css`
  background-color: #3d3d3d;
  border-radius: ${borderRadius};
  transform: translate(2rem, -50%) !important;
  left: unset !important;
`;

export const BasemapSelectBase = ({ items, baseMapName, updateMap }: IBaseMapSelectProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("mapControls");
  const theme = useTheme() as ITheme;
  const { borderRadius } = theme;
  const isShared = useIsShared();
  const [isOpenBaseMapBlock, toggleBaseMapBlock] = useToggle(false);
  const [isAddMenuOpen, toggleAddMenuOpen] = useToggle(false);
  const { isNirvanaMode } = useNirvanaMode();
  const isDashboard = useIsDashboard();

  const options = [
    { text: t("fromLayersCatalog"), value: "fromLayersCatalog" },
    { text: t("solidColor"), value: "fillWithColor" },
  ];

  const handlers = useAddBasemapHandlers();

  const onSelect = useCallback(
    (option: IOption[]) => {
      const [{ value }] = option;

      if (handlers[value]) {
        handlers[value]();
      }

      isAddMenuOpen && toggleAddMenuOpen();
    },
    [isAddMenuOpen, handlers, toggleAddMenuOpen],
  );

  const prevIsNirvanaMode = useRef(isNirvanaMode);

  useEffect(() => {
    if (isOpenBaseMapBlock && prevIsNirvanaMode.current !== isNirvanaMode) {
      toggleBaseMapBlock();
    }
    prevIsNirvanaMode.current = isNirvanaMode;
  }, [isNirvanaMode]);

  const showMapLegend = useSelector(getShowMapLegend);

  const toggleLegend = useCallback(() => {
    dispatch(toggleMapLegend());
  }, [dispatch]);

  useEffect(() => {
    if (showMapLegend && isOpenBaseMapBlock) {
      toggleBaseMapBlock();
    }
  }, [showMapLegend]);

  useEffect(() => {
    if (showMapLegend && isOpenBaseMapBlock) {
      toggleLegend();
    }
  }, [isOpenBaseMapBlock]);

  useEffect(() => {
    if (showMapLegend) {
      toggleLegend();
    }
  }, [isDashboard]);

  return (
    <MapControlContainer withPadding>
      <Legend showLegend={showMapLegend} toggleLegend={toggleLegend} />
      <Popover
        anchor={
          <IconToggle
            title={t("chooseBasemap")}
            kind={"basemap"}
            isSelected={isOpenBaseMapBlock}
            onClick={toggleBaseMapBlock}
          />
        }
        open={isOpenBaseMapBlock}
        onRequestClose={toggleBaseMapBlock}
      >
        <BasemapListContainer>
          <BasemapListTitle>
            <H3>{t("basemapListTitle")}</H3>
            {!isShared && (
              <Popover
                anchor={<IconButton kind={"feature_add"} onClick={toggleAddMenuOpen} />}
                autoPosition={false}
                animateAppearance
                targetOrigin="right"
                anchorOrigin="right"
                open={isAddMenuOpen}
                onRequestClose={toggleAddMenuOpen}
                closeOnScroll={false}
                mixin={getPopoverMixin(borderRadius.smallest)}
                zIndex={99}
              >
                <Menu options={options} selectOption={onSelect} />
              </Popover>
            )}
          </BasemapListTitle>
          <BasemapListBlock>
            {items &&
              items.map(baseMapItem => (
                <BasemapBlock
                  key={baseMapItem.name}
                  baseMapItem={baseMapItem}
                  onClick={() => updateMap(baseMapItem.name)}
                  selected={baseMapItem.name === baseMapName}
                />
              ))}
          </BasemapListBlock>
        </BasemapListContainer>
      </Popover>
    </MapControlContainer>
  );
};
